import type { QuestTemplateFirestore } from '../services/questTypes';

/**
 * Type for tracking which resources are used and where
 * Maps resourceId to an array of journeyDayIds where it's used
 */
export type ResourceUsageMap = Record<string, string[]>;

/**
 * Builds a usage map from quest templates for a specific resource type
 * @param questTemplates - All quest templates to analyze
 * @param resourceKey - The key to extract from quest templates (lessonId, questionId, etc.)
 * @returns Map of resourceId to journeyDayIds where it's used
 */
export const buildResourceUsageMap = (
  questTemplates: QuestTemplateFirestore[],
  resourceKey: 'lessonId' | 'questionId' | 'successStoryId' | 'toolId'
): ResourceUsageMap => {
  const usageMap: ResourceUsageMap = {};

  questTemplates.forEach((template) => {
    let resourceId: string | undefined;
    let journeyDayId: string | undefined;

    // Extract the resource ID based on quest type
    if (template.type === 'lesson' && resourceKey === 'lessonId') {
      resourceId = template.lessonId;
      journeyDayId = template.journeyDayId;
    } else if (template.type === 'community-question' && resourceKey === 'questionId') {
      resourceId = template.questionId;
      journeyDayId = template.journeyDayId;
    } else if (template.type === 'story' && resourceKey === 'successStoryId') {
      resourceId = template.successStoryId;
      journeyDayId = template.journeyDayId;
    } else if (template.type === 'tool' && resourceKey === 'toolId') {
      resourceId = template.toolId;
      journeyDayId = template.journeyDayId;
    }

    // Add to usage map if we found a valid resource ID and journey day
    if (resourceId && journeyDayId) {
      if (!usageMap[resourceId]) {
        usageMap[resourceId] = [];
      }
      if (!usageMap[resourceId].includes(journeyDayId)) {
        usageMap[resourceId].push(journeyDayId);
      }
    }
  });

  return usageMap;
};

/**
 * Formats journey day IDs into readable day numbers
 * @param journeyDayIds - Array of journey day IDs (e.g., ["day_1", "day_3"])
 * @returns Formatted string (e.g., "Day 1, Day 3")
 */
export const formatJourneyDays = (journeyDayIds: string[]): string => {
  const dayNumbers = journeyDayIds
    .map((id) => {
      const match = id.match(/day_(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((num): num is number => num !== null)
    .sort((a, b) => a - b);

  return dayNumbers.map((num) => `Day ${num}`).join(', ');
};
